import { useAuthStore } from "@/stores/authStore";
import {
  ShoppingCart,
  Users,
  Plane,
  TrendingUp,
  Calendar,
  Clock,
  AlertCircle,
} from "lucide-react";

// Placeholder data - will be replaced with real API calls
const stats = [
  {
    name: "Nové poptávky",
    value: "12",
    change: "+3 dnes",
    icon: ShoppingCart,
    color: "primary",
  },
  {
    name: "Aktivní zájezdy",
    value: "45",
    change: "8 odjíždí tento měsíc",
    icon: Plane,
    color: "accent",
  },
  {
    name: "Zákazníci",
    value: "1,234",
    change: "+24 tento měsíc",
    icon: Users,
    color: "success",
  },
  {
    name: "Tržby tento měsíc",
    value: "2.4M Kč",
    change: "+15% oproti minulému",
    icon: TrendingUp,
    color: "warning",
  },
];

const recentInquiries = [
  {
    id: "1",
    customer: "Jan Novák",
    trip: "Bali - Krásy ostrova",
    date: "2 min",
    status: "new",
  },
  {
    id: "2",
    customer: "Petra Svobodová",
    trip: "Vietnam - Velká cesta",
    date: "15 min",
    status: "new",
  },
  {
    id: "3",
    customer: "Martin Horák",
    trip: "Japonsko - Období sakur",
    date: "1 hod",
    status: "contacted",
  },
  {
    id: "4",
    customer: "Eva Králová",
    trip: "Peru - Po stezkách Inků",
    date: "2 hod",
    status: "quoted",
  },
];

const upcomingDepartures = [
  {
    trip: "Bali - Krásy ostrova",
    date: "15.3.2026",
    participants: "12/14",
    guide: "Radek Dymeš",
  },
  {
    trip: "Vietnam - Velká cesta",
    date: "21.3.2026",
    participants: "14/16",
    guide: "Martina Nováková",
  },
  {
    trip: "Japonsko - Období sakur",
    date: "28.3.2026",
    participants: "14/16",
    guide: "Jan Horák",
  },
];

export default function DashboardPage() {
  const { user } = useAuthStore();

  const getColorClasses = (color: string) => {
    switch (color) {
      case "primary":
        return "bg-primary-100 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400";
      case "accent":
        return "bg-accent-100 text-accent-600 dark:bg-accent-900/30 dark:text-accent-400";
      case "success":
        return "bg-success-100 text-success-600 dark:bg-success-900/30 dark:text-success-400";
      case "warning":
        return "bg-warning-100 text-warning-600 dark:bg-warning-900/30 dark:text-warning-400";
      default:
        return "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400";
    }
  };

  return (
    <div className="space-y-6">
      {/* Welcome */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Dobrý den, {user?.name?.split(" ")[0]}!
        </h1>
        <p className="mt-1 text-gray-600 dark:text-gray-400">
          Zde je přehled vašeho účtu.
        </p>
      </div>

      {/* Stats grid */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => (
          <div key={stat.name} className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  {stat.name}
                </p>
                <p className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">
                  {stat.value}
                </p>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {stat.change}
                </p>
              </div>
              <div className={`rounded-full p-3 ${getColorClasses(stat.color)}`}>
                <stat.icon className="h-6 w-6" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Two columns */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Recent inquiries */}
        <div className="card">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Poslední poptávky
            </h2>
            <a
              href="/orders"
              className="text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400"
            >
              Zobrazit vše →
            </a>
          </div>

          <div className="space-y-3">
            {recentInquiries.map((inquiry) => (
              <div
                key={inquiry.id}
                className="flex items-center justify-between rounded-lg border border-gray-100 p-3 dark:border-gray-700"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100 font-medium text-primary-700 dark:bg-primary-900/30 dark:text-primary-400">
                    {inquiry.customer
                      .split(" ")
                      .map((n) => n[0])
                      .join("")}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {inquiry.customer}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {inquiry.trip}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400">
                    <Clock className="h-4 w-4" />
                    {inquiry.date}
                  </span>
                  {inquiry.status === "new" && (
                    <span className="badge-primary">Nová</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Upcoming departures */}
        <div className="card">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Blížící se odjezdy
            </h2>
            <a
              href="/trips"
              className="text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400"
            >
              Zobrazit vše →
            </a>
          </div>

          <div className="space-y-3">
            {upcomingDepartures.map((departure, index) => (
              <div
                key={index}
                className="flex items-center justify-between rounded-lg border border-gray-100 p-3 dark:border-gray-700"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-accent-100 text-accent-700 dark:bg-accent-900/30 dark:text-accent-400">
                    <Calendar className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {departure.trip}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {departure.guide}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-medium text-gray-900 dark:text-white">
                    {departure.date}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {departure.participants} osob
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Alerts / Reminders */}
      <div className="card border-warning-200 bg-warning-50 dark:border-warning-800 dark:bg-warning-900/20">
        <div className="flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-warning-600 dark:text-warning-400" />
          <div>
            <h3 className="font-medium text-warning-800 dark:text-warning-300">
              Připomínky
            </h3>
            <ul className="mt-2 space-y-1 text-sm text-warning-700 dark:text-warning-400">
              <li>• 3 nezaplacené zálohy se splatností do 3 dnů</li>
              <li>• 2 objednávky čekají na potvrzení více než 48 hodin</li>
              <li>• 5 zájezdů potřebuje aktualizovat termíny pro rok 2027</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
